import { useMemo } from "react";
import { ContainerSelectionState } from "../types";
import { useSelection } from "./useSelection";

/**
 * 컨테이너 선택 상태를 관리하는 커스텀 훅
 *
 * 내부적으로 제네릭 useSelection 훅을 사용하여 구현됩니다.
 * Set을 사용한 O(1) 조회로 성능을 최적화하고,
 * allContainerIds 변경 시 stale selection을 자동으로 정리합니다.
 *
 * @param allContainerIds - 선택 가능한 모든 컨테이너 ID 배열
 * @returns ContainerSelectionState - 선택 상태 및 조작 함수들
 *
 * @example
 * const containerList = [{ id: 1, containerNo: 'CONT001' }, { id: 2, containerNo: 'CONT002' }];
 * const allIds = containerList.map(c => c.id);
 * const selection = useContainerSelection(allIds);
 *
 * // 개별 선택/해제
 * selection.toggleContainer(1);
 *
 * // 전체 선택/해제
 * selection.addAllContainers();
 * selection.removeAllContainers();
 *
 * // 선택된 컨테이너 ID 배열
 * console.log(selection.selectedContainers); // [1, 2]
 */
export const useContainerSelection = (
  allContainerIds: number[]
): ContainerSelectionState => {
  const {
    selectedItems,
    addItem,
    removeItem,
    addAllItems,
    removeAllItems,
    toggleItem,
    isAllSelected,
  } = useSelection(allContainerIds);

  // Container-specific naming으로 매핑
  return useMemo(
    () => ({
      selectedContainers: selectedItems,
      addContainer: addItem,
      removeContainer: removeItem,
      addAllContainers: addAllItems,
      removeAllContainers: removeAllItems,
      toggleContainer: toggleItem,
      isAllSelected,
    }),
    [
      selectedItems,
      addItem,
      removeItem,
      addAllItems,
      removeAllItems,
      toggleItem,
      isAllSelected,
    ]
  );
};
